import { z } from "zod";
/**
 * Contrato front <-> back de la CAEs Payments Console.
 * Fichero conjunto (Rubén/Javi) — ver CLAUDE.md "Contrato front ↔ back" y "Colisiones resueltas".
 * Cambio de esquema aquí = PR propio, sin implementación dentro, avisando al otro lado.
 */
const dateStringPattern = /^\d{4}-\d{2}-\d{2}$/;
const DateString = z.string().regex(dateStringPattern, "esperado 'YYYY-MM-DD'");
/**
 * Un clic en la tabla dinámica CA × semana.
 *
 * Existe porque la selección de celdas NO se puede expresar con las tres listas de criterios: esas
 * se combinan con AND entre dimensiones, así que clicar (Galicia, 4 may) y (C. Valenciana, 11 may)
 * arrastraría también los dos cruces que nadie clicó. Aquí cada patrón es una celda, una fila o una
 * columna, y **los patrones se combinan entre sí con OR**: se selecciona exactamente lo clicado.
 *
 * | clic en… | patrón |
 * |---|---|
 * | una celda | `{ regionName, semanaInicio }` |
 * | la cabecera de una fila (una CA) | `{ regionName }` — cualquier semana |
 * | la cabecera de una columna (una semana) | `{ semanaInicio }` — cualquier CA |
 *
 * `regionName: null` es el bucket "sin CA", que aquí SÍ se puede aislar (a diferencia del filtro
 * `regionName`, donde seleccionar CAs concretas lo deja fuera por la semántica de la lista).
 *
 * Un patrón vacío no significa nada, así que se rechaza: al menos uno de los dos campos presente.
 */
const PatronSeleccionSchema = z
    .object({
    regionName: z.string().min(1).nullable().optional(),
    /** Lunes de la semana ISO, igual que `porCaSemana.semanaInicio`. */
    semanaInicio: DateString.optional(),
    /**
     * Primer día del mes (`'2026-04-01'`), para filtrar un MES CALENDARIO exacto.
     *
     * Existe porque una lista de semanas no es un mes: la semana del 27 de abril arrastra tres días
     * de mayo, y la del 30 de marzo se lleva cinco días de abril al mes anterior. Agrupando semanas,
     * clicar "abril" seleccionaba del 6 de abril al 3 de mayo — el pill lo confesaba y el filtro no
     * era lo que nadie había pedido. Con este patrón, abril es del 1 al 30 de abril y nada más.
     *
     * Es un DateString y no un `'YYYY-MM'` para no meter un segundo formato de fecha en el contrato:
     * el backend lo traduce a un rango semiabierto `>= mesInicio AND < mesInicio + 1 mes`.
     */
    mesInicio: DateString.optional(),
})
    .strict()
    .refine((p) => p.regionName !== undefined || p.semanaInicio !== undefined || p.mesInicio !== undefined, { message: "un patrón de selección necesita regionName, semanaInicio o mesInicio" })
    // Semana y mes a la vez no significa nada: son dos formas de acotar la misma dimensión, y su
    // intersección o es la semana o es vacía. Se rechaza en vez de dejar que el backend elija.
    .refine((p) => !(p.semanaInicio !== undefined && p.mesInicio !== undefined), {
    message: "un patrón no puede llevar semanaInicio y mesInicio a la vez",
})
    /**
     * `mesInicio` tiene que ser el DÍA 1. La UI nunca genera otra cosa —la clave de la columna sale de
     * `DATE_TRUNC(..., MONTH)`— así que esto protege del camino a mano, que en esta consola existe: las
     * 6 cuentas con el claim pueden llamar a la callable directamente.
     *
     * Sin la guarda, `'2026-04-15'` filtra 15 abr → 15 may (y `DATE_ADD(..., INTERVAL 1 MONTH)` sobre un
     * día 31 recorta a fin de mes, con lo que la ventana ni siquiera mide un mes), mientras el chip y el
     * audit log lo etiquetan como "abril de 2026". Lo que se paga sigue siendo lo previsualizado —el
     * confirm solo recibe `previewId`—, pero el registro describiría mal el filtro, que es el invariante
     * 4. Con el día 1 garantizado, esa etiqueta es exacta por construcción.
     *
     * RECHAZA, nunca corrige: truncar `'2026-04-15'` a `'2026-04-01'` ampliaría el conjunto 14 días en
     * silencio, que es peor que el problema. Un criterio malformado falla duro.
     */
    .refine((p) => p.mesInicio === undefined || p.mesInicio.endsWith("-01"), {
    message: "mesInicio tiene que ser el primer día del mes ('YYYY-MM-01')",
});
// Multivalor o ausente. Ausente/vacío = "todas" (incluye filas sin region_name / sin rango).
// Con valores = solo esos (filas sin region_name quedan fuera si hay CA seleccionada).
export const CriteriosSchema = z
    .object({
    regionName: z.array(z.string().min(1)).optional(),
    rangoFechas: z
        .object({
        desde: DateString,
        hasta: DateString,
    })
        .strict()
        .optional(),
    // Vocabulario lo sirve el backend (no se hardcodea): valor libre no vacío, no un enum.
    caesMitecoStatus: z.array(z.string().min(1)).optional(),
    /**
     * Fichero .xlsx de MITECO que certificó el viaje (2026-09-08, trazabilidad hacia la remesa de
     * origen). Igual que `caesMitecoStatus`: vocabulario libre servido por el backend, nunca un
     * enum. Con valores seleccionados, los viajes aún no certificados (el centinela
     * `'No Certificados MITECO'` que pone dbt) quedan fuera — no hace falta ningún bucket especial
     * porque el centinela es un string más, nunca NULL.
     */
    driveFileName: z.array(z.string().min(1)).optional(),
    /** Igual que `driveFileName`, pero por la remesa (subcarpeta Drive) que agrupa varios ficheros. */
    certificationPeriod: z.array(z.string().min(1)).optional(),
    /**
     * BB-2022 (2026-09-09): qué universo de viajes consulta `/preview` — nunca un nombre de tabla
     * que mande el cliente, un enum cerrado que el backend resuelve a su propia config. Ausente =
     * `'elegibles'` (el comportamiento de siempre, sin cambios).
     *
     * `'softKiller'` apunta a `int_caes_console_soft_killer_pagable` (repo data-team-dbt, BB-2003):
     * viajes que Data rechaza por un soft killer pero cuyo `caes_payment_state` ya es
     * `BLOCKED_BY_CERTIFICATION`. Pagables desde el 2026-10-09 (BB-2003, decisión de Rubén con Ops):
     * `/confirm` acepta un preview de este programa igual que uno de `elegibles`, y nunca los dos
     * en vuelo a la vez. Solo `previewSoftKillerCallable` lo fija; el backend lo fuerza ahí.
     */
    programa: z.enum(["elegibles", "softKiller"]).optional(),
    /**
     * Motivo de soft-killer (BB-2022), solo tiene efecto con `programa: 'softKiller'` — ignorado
     * si no existe la columna en el programa consultado (guard de columna en `queryBuilder.ts`).
     * Vocabulario libre servido por el backend, igual que `caesMitecoStatus`: valores hoy incluyen
     * los `motivo_principal`/`motivo_principal_rechazo_qa` de `int_caes_console_soft_killer_pagable`
     * (dbt, repo data-team-dbt), y esa lista puede crecer sin tocar este contrato.
     */
    caesSoftKillerReason: z.array(z.string().min(1)).optional(),
    /**
     * Conductor en lista negra (BB-2607, 2026-09-30). Tres estados, no un enum: ausente = todos
     * (el comportamiento de siempre, sin filtro), `true` = solo viajes con conductor en blacklist,
     * `false` = excluye esos viajes. Aplica a los dos programas; el backend lo ignora si la columna
     * `is_driver_blacklisted` no existe en la tabla del programa (guard de columna en
     * `queryBuilder.ts`). Es el flag ACTUAL de dbt en el momento del build, no el de la fecha del
     * viaje — el snapshot del preview lo congela, así que lo pagado sigue siendo lo previsualizado.
     */
    isDriverBlacklisted: z.boolean().optional(),
    /**
     * Selección hecha clicando en la tabla CA × semana. Los patrones se combinan entre sí con **OR**,
     * y el resultado se combina con los otros tres criterios con **AND** — igual que ellos entre sí.
     *
     * O sea: `(CA ∈ regionName) Y (fecha ∈ rangoFechas) Y (estado ∈ caesMitecoStatus) Y (algún
     * patrón de selección encaja)`. Ausente o vacío = no restringe nada.
     */
    seleccion: z.array(PatronSeleccionSchema).optional(),
})
    .strict();
const DesgloseCaSchema = z
    .object({
    regionName: z.string().min(1).nullable(), // null = bucket "sin CA"
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(), // NUMERIC de BigQuery viaja como string, nunca float de JS
})
    .strict();
const DesglosePorMitecoStatusSchema = z
    .object({
    caesMitecoStatus: z.string().min(1),
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
const DesglosePorPaymentStateSchema = z
    .object({
    caesPaymentState: z.string().min(1).nullable(), // null = bucket NULL
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
/**
 * Desglose por fichero de certificación MITECO (2026-09-08, `int_caes_console_elegibles`).
 *
 * `driveFileName` nunca es NULL: dbt aplica `COALESCE(..., 'No Certificados MITECO')` para los 4
 * estados elegibles que aún no pasaron por MITECO, así que ese string es un valor más de la lista,
 * no un bucket especial — a diferencia de `regionName`/`caesPaymentState`, aquí no hace falta
 * `.nullable()`.
 */
const DesglosePorDriveFileNameSchema = z
    .object({
    driveFileName: z.string().min(1),
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
/** Igual que el desglose por fichero, pero por la remesa (subcarpeta Drive) que lo contiene. */
const DesglosePorCertificationPeriodSchema = z
    .object({
    certificationPeriod: z.string().min(1),
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
/**
 * Desglose CA × semana (2026-08-12), para la tabla dinámica del resumen: filas CA, columnas
 * semana, dato el importe — "en Galicia, la semana del 4 de mayo se pagarían X €".
 *
 * `semanaInicio` es el LUNES de la semana ISO en `'YYYY-MM-DD'`, no un número de semana: un
 * `2026-W19` obliga a quien lo lee a traducirlo, y el lunes ya es una fecha que se puede comparar
 * con el filtro que acabas de aplicar. La semana va cerrada por ISO (lunes a domingo) para que no
 * dependa de la configuración regional de nadie.
 *
 * Lo produce el backend, como el resto del resumen: el front no recibe ninguna fila de viaje, solo
 * agregados, así que no podría calcular esto sin inventarse el conjunto entero.
 */
const DesglosePorCaSemanaSchema = z
    .object({
    regionName: z.string().min(1).nullable(), // null = bucket "sin CA"
    semanaInicio: DateString,
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
/**
 * Desglose por motivo/origen de rechazo, solo para `programa: 'softKiller'` (BB-2022/BB-2004).
 * Con `programa: 'elegibles'` (o ausente) el backend los devuelve vacíos — no hay motivo de
 * soft-killer que desglosar sobre el universo elegible.
 */
const DesglosePorSoftKillerReasonSchema = z
    .object({
    caesSoftKillerReason: z.string().min(1),
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
/**
 * Igual que `porSoftKillerReason`, pero por mecanismo de origen del rechazo (`'F1_LIMITES'` se
 * reevalúa solo cada 12h; `'QA'` no tiene reevaluación automática salvo `NOMBRE_INVALIDO_QA`,
 * ver `caes_qa_liberar_nombre_invalido`, repo data-team-dags) — determina si Ops puede simplemente
 * esperar o si hace falta intervención manual.
 */
const DesglosePorSoftKillerOrigenSchema = z
    .object({
    caesSoftKillerOrigen: z.string().min(1),
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
/** Desglose por conductor en lista negra (BB-2607): como mucho dos buckets, `true` y `false`. */
const DesglosePorIsDriverBlacklistedSchema = z
    .object({
    isDriverBlacklisted: z.boolean(),
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
/** Igual que el desglose semanal, pero la columna es el primer día del mes. */
const DesglosePorCaMesSchema = z
    .object({
    regionName: z.string().min(1).nullable(),
    mesInicio: DateString,
    nViajes: z.number().int().nonnegative(),
    importeEur: z.string(),
})
    .strict();
export const ResumenSchema = z
    .object({
    importeTotalEur: z.string(),
    nViajes: z.number().int().nonnegative(),
    nViajesSinImporte: z.number().int().nonnegative(),
    rangoFechasEfectivo: z
        .object({ min: DateString, max: DateString })
        .strict()
        .nullable(), // null si el conjunto está vacío
    porCa: z.array(DesgloseCaSchema),
    porMitecoStatus: z.array(DesglosePorMitecoStatusSchema),
    porPaymentState: z.array(DesglosePorPaymentStateSchema),
    /**
     * Desglose por fichero/remesa de certificación MITECO. Sin tope a propósito, a diferencia de
     * `porCaSemana`/`porCaMes`: son 66 ficheros y 6 periodos en ~8 meses de histórico (medido
     * 2026-09-09), órdenes de magnitud por debajo del límite de 1 MB del documento de Firestore. Si
     * el crecimiento cambia de escala, esto necesitará el mismo patrón de tope + "omitidos".
     */
    porDriveFileName: z.array(DesglosePorDriveFileNameSchema),
    porCertificationPeriod: z.array(DesglosePorCertificationPeriodSchema),
    /**
     * Solo con `criterios.programa === 'softKiller'` (BB-2022/BB-2004). OPCIONAL por el mismo
     * motivo que `presupuestoDiario` más abajo: mientras el backend no implemente BB-2022 no
     * rellena este campo, y si fuera obligatorio `/preview` reventaría en cada llamada desde que
     * este PR de contrato se desplegara. Ausente o `[]` = no hay nada que desglosar (universo
     * `elegibles`, o `softKiller` antes de que el backend lo popule).
     */
    porSoftKillerReason: z.array(DesglosePorSoftKillerReasonSchema).optional(),
    /** Igual que `porSoftKillerReason`, ver esa nota. */
    porSoftKillerOrigen: z.array(DesglosePorSoftKillerOrigenSchema).optional(),
    /**
     * BB-2607. OPCIONAL por el mismo motivo que `porSoftKillerReason`: si fuera obligatorio,
     * `/preview` reventaría desde que se desplegara este contrato hasta que el backend lo rellene.
     * Ausente = backend sin desplegar todavía; el front no pinta la distribución.
     */
    porIsDriverBlacklisted: z.array(DesglosePorIsDriverBlacklistedSchema).optional(),
    porCaSemana: z.array(DesglosePorCaSemanaSchema),
    /**
     * El mismo cruce pero por MES CALENDARIO, para la vista mensual de la tabla dinámica.
     *
     * Va aparte y no se deriva de `porCaSemana` en el front porque no se puede: agrupar semanas da
     * un mes que no es el mes. Y tiene que existir para que la columna que Ops ve sume exactamente
     * lo que el patrón `mesInicio` va a seleccionar — si la vista agrupara semanas y el filtro fuera
     * el mes exacto, la cifra de la pantalla y la del pago no coincidirían, que es peor que el
     * problema que esto resuelve.
     *
     * `mesInicio` es el primer día del mes, el mismo valor que acepta el patrón de selección.
     */
    porCaMes: z.array(DesglosePorCaMesSchema),
    /** Meses más antiguos que no vienen en `porCaMes`, por el mismo tope que `semanasOmitidas`. */
    mesesOmitidos: z.number().int().nonnegative(),
    /**
     * Semanas MÁS ANTIGUAS que no vienen en `porCaSemana`, porque el desglose está acotado a las
     * más recientes (ver MAX_SEMANAS_PIVOTE en el backend).
     *
     * El tope existe porque este desglose es el único del resumen cuyo tamaño crece con el rango
     * de fechas: 20 CCAA × N semanas. Un preview sin filtros abarca años, y el resumen entero se
     * guarda en un documento de Firestore (tope 1 MB) tanto en `previews` como en el audit log —
     * sin tope, un preview amplio no se podría ni guardar.
     *
     * Y viaja explícito, en vez de recortar en silencio: la UI tiene que poder decir que faltan
     * semanas. Un cero significa que la tabla está completa, no que no se sepa.
     */
    semanasOmitidas: z.number().int().nonnegative(),
})
    .strict();
export const BackendModeSchema = z.enum(["mock", "real"]);
/**
 * Presupuesto de peticiones del endpoint de pago (BB-1667 Fase 2).
 *
 * Existe porque **una confirmación cuesta una petición por viaje**, y el endpoint tiene una cuota que
 * hasta el 2026-08-13 no sabíamos que existía: `Limit::perDay` del limitador `admin` de `hoop-api`,
 * hoy 750.000 (300.000 hasta el 2026-09-29). Aquella confirmación de 342.185 viajes chocó con ella a las ~5 h de arrancar y, con el
 * código de entonces, cada chunk rechazado se llevaba 200 viajes por delante.
 *
 * La pantalla tiene que poder decir esto ANTES de que alguien pulse: no cuánto dinero mueve la
 * selección —eso ya lo dice— sino **cuántas peticiones cuesta y si caben**. Una selección de 4,5 M de
 * viajes son 7 ventanas: no es que vaya lenta, es que no cabe en un día por mucho que se acelere.
 *
 * ⚠️ **La ventana NO es un día natural.** `Limit::perDay` de Laravel es una ventana fija de 24 h
 * anclada a la primera petición, y ese ancla no se puede observar. Por eso el ledger cuenta en ventana
 * deslizante y viaja `ventanaTerminaEn` en vez de un "hoy": la UI puede decir "se libera cuota a las
 * 18:45", que es verdad, y no "quedan X hoy", que sería mentira.
 */
const PresupuestoDiarioSchema = z
    .object({
    /** Peticiones por ventana. Configurado por debajo del límite real, para dejar aire a reintentos. */
    limite: z.number().int().positive(),
    /**
     * Peticiones reservadas que cuentan ahora mismo: las de las últimas 24 h. Desde el 2026-09-29 el
     * ledger cuenta en ventana deslizante (ver `presupuesto.ts`), así que no hay "ventana en curso".
     */
    gastado: z.number().int().nonnegative(),
    /**
     * Lo que cabría en una tanda que empezara ya, contando también las reservas FUTURAS de las próximas
     * 24 h con las que se solaparía. Por eso `gastado + restante` puede ser menor que `limite`.
     */
    restante: z.number().int().nonnegative(),
    /**
     * Cuándo caduca la primera reserva que cuenta ahora (ISO 8601), o sea cuándo se libera algo. `null`
     * si no cuenta ninguna — aunque puede haber reservas futuras, así que `null` ya NO significa
     * "presupuesto entero": eso lo dice `restante`.
     */
    ventanaTerminaEn: z.string().nullable(),
    /**
     * Peticiones que necesitaría confirmar esta selección: una por viaje. Es un MÍNIMO — los
     * reintentos de 429/5xx también consumen cuota, y por eso el límite configurado va por debajo del
     * real.
     */
    peticionesQueNecesita: z.number().int().nonnegative(),
})
    .strict();
// POST /preview response
export const PreviewResponseSchema = z
    .object({
    previewId: z.string().min(1),
    resumen: ResumenSchema,
    criterios: CriteriosSchema,
    mode: BackendModeSchema,
    // BB-1551: cuándo dbt escribió la fila más reciente del conjunto (ISO 8601), vía
    // MAX(dbt_processed_at) sobre el snapshot. Ni el pago de Backend ni la apertura de Ops
    // tienen hora fija — no hay "hora segura" que memorizar, así que la consola muestra el hecho
    // en vez de una hora. `null` es esperado y frecuente: cualquier filtro sin resultados da
    // MAX sobre un conjunto vacío.
    datosActualizadosEn: z.string().nullable(),
    /**
     * BB-1651: el límite teórico de peticiones/segundo al endpoint de pago real
     * (`CAES_PAYMENT_MAX_RPS`), para que el front calcule un ETA de confirmación sin duplicar la
     * constante. `tiempoEstimadoSegundos = nViajes / limiteRpsPago` es un MÍNIMO, no una cifra
     * exacta: reintentos (429/5xx) y latencia real de red solo pueden alargarlo, nunca acortarlo.
     * Viaja en /preview y no en una constante del front porque el número vive en
     * `functions/.env` y puede cambiar sin un deploy del front.
     */
    limiteRpsPago: z.number().int().positive(),
    /**
     * BB-1667 Fase 2: el presupuesto de peticiones y lo que esta selección costaría. Va en `/preview`
     * y no en una llamada aparte porque se lee en el mismo momento que las cifras que decide Ops: si
     * fuera otra petición, la pantalla podría mostrar un importe de un conjunto y un presupuesto de
     * otro instante.
     */
    /**
     * OPCIONAL a propósito, y no por comodidad. Dos razones:
     *
     * 1. La respuesta se construye con `PreviewResponseSchema.parse(...)`, que recibe `unknown`: un
     *    campo obligatorio que el backend todavía no rellena **no lo ve TypeScript** y revienta en
     *    ejecución, en CADA preview. Con el campo obligatorio, este PR de contrato dejaría `/preview`
     *    roto hasta que llegara la implementación.
     * 2. Resistencia al orden de despliegue: si un deploy de Hosting llega antes que el de las
     *    functions, el front tiene que degradar a "no lo sé" en vez de tumbar la pantalla. Un dato de
     *    infraestructura es justo el tipo de campo que conviene que sea ausente-tolerante.
     *
     * La regla del front: ausente significa **"no se sabe"**, nunca "cero" ni "hay de sobra". Si no
     * está, no se pinta el presupuesto — no se inventa uno.
     */
    presupuestoDiario: PresupuestoDiarioSchema.optional(),
})
    .strict();
// POST /confirm request — y nada más: ni criterios, ni ids, ni cifras.
export const ConfirmRequestSchema = z
    .object({
    previewId: z.string().min(1),
})
    .strict();
// BB-1617 (2026-08-11): /confirm deja de ser síncrono. A 40 req/s por viaje, una selección
// grande no cabe en el timeout de una callable (Cataluña sola: 59 min; el máximo posible de una
// función gen2 son 144.000 viajes, ni una CA grande entra) — ver CLAUDE.md "Abierto". /confirm
// ahora encola y devuelve al momento; el resultado real se sigue por el audit log (evento
// 'progress', pedido en el mismo ticket), que el front ya lee directo con el SDK de cliente.
export const ConfirmResponseSchema = z
    .object({
    previewId: z.string().min(1),
    // 'completado' solo cuando la selección deduplicada está vacía (nada que encolar) — el único
    // caso en que /confirm puede resolver sin dejar trabajo pendiente en la cola.
    estado: z.enum(["encolado", "completado"]),
    totalViajes: z.number().int().nonnegative(),
    totalChunks: z.number().int().nonnegative(),
})
    .strict();
/**
 * POST /cancelar — BB-1676. Detiene lo que queda por pagar de una confirmación.
 *
 * **Cancela el JOB, nunca el registro.** El audit log es insert-only (invariante 6) y una cancelación
 * es un hecho más que se añade, no un documento que desaparezca: quién la pidió y cuándo quedan
 * escritos igual que la confirmación que la provocó. Borrar el rastro de una decisión de dinero es
 * exactamente lo que un audit log existe para impedir.
 *
 * Manda solo el `previewId`, igual que /confirm y por el mismo motivo: el cliente no elige qué se
 * cancela más allá de señalar cuál, y no hay ningún parámetro con el que pedir otra cosa.
 */
export const CancelarRequestSchema = z
    .object({
    previewId: z.string().min(1),
})
    .strict();
export const CancelarResponseSchema = z
    .object({
    previewId: z.string().min(1),
    /**
     * `ya-estaba-cancelada` no es un error: el doble clic y el reintento del cliente tienen que ser
     * inocuos, así que la segunda llamada informa en vez de fallar. Lo que sí es un error —y llega
     * como `HttpsError`, no por aquí— es cancelar algo ajeno, algo que ya terminó, o algo que no
     * existe.
     */
    estado: z.enum(["cancelada", "ya-estaba-cancelada"]),
    /**
     * Tandas que quedaban sin procesar en el momento de cancelar, o sea lo que esta cancelación
     * evita. **No es una garantía de viajes salvados:** una tanda que ya está pagando termina lo que
     * empezó (la cancelación se comprueba al empezar cada tanda, no viaje a viaje), así que el número
     * real puede ser una tanda menos.
     */
    tandasPendientes: z.number().int().nonnegative(),
    /**
     * Viajes ya enviados al endpoint de pago cuando se canceló. Este dinero **ya se movió** y
     * cancelar no lo deshace: la consola no tiene forma de retirar una certificación aceptada. Va en
     * la respuesta para que la pantalla lo pueda decir con la cifra delante, en vez de con un aviso
     * genérico.
     */
    nViajesYaEnviados: z.number().int().nonnegative(),
})
    .strict();
/**
 * POST /ficheros — BB-2180. Ficheros de Drive que la consola está viendo, con su fecha de carga y
 * el tag ÚLTIMO, para el bloque bajo el filtro Periodo | Fichero en la pestaña Elegibles.
 *
 * Universo COMPLETO (nunca el filtro aplicado): mismo criterio de elegibilidad que la tabla base
 * (`int_caes_console_elegibles`), no el de la selección que Ops tenga puesta en pantalla — con el
 * filtro en Andalucía, una lista que solo trajera ficheros de Andalucía leería como "faltan los de
 * Castilla y León" cuando no es así. Por eso es una callable de solo lectura aparte, no un campo de
 * `/preview`.
 *
 * Sin request payload: no hay ningún criterio que aplicar, así que el objeto va vacío y `.strict()`
 * rechaza cualquier campo que alguien intente colar.
 */
export const FicherosRequestSchema = z.object({}).strict();
export const FicheroLeidoSchema = z
    .object({
    driveFileName: z.string().min(1),
    certificationPeriod: z.string().min(1),
    /** ISO 8601 — `MAX(drive_file_loaded_at)` de ese fichero (dbt, `int_caes_console_elegibles`). */
    cargadoEn: z.string(),
    /** Viajes de ese fichero que la consola ve HOY (con trayectos aún pagables). */
    nViajes: z.number().int().nonnegative(),
    /**
     * Cargado el mismo día de calendario de Madrid que el fichero más reciente. Si pasan varios
     * días sin ficheros nuevos, el tag se queda en el último lote que entró — no es un aviso de
     * frescura, por eso `leidoEn` no lleva umbral de aviso (ver más abajo).
     */
    ultimo: z.boolean(),
})
    .strict();
export const FicherosResponseSchema = z
    .object({
    /** ISO 8601 — `MAX(drive_file_loaded_at)` del conjunto completo. `null` si no hay ficheros. */
    leidoEn: z.string().nullable(),
    ficheros: z.array(FicheroLeidoSchema),
})
    .strict();
/**
 * BB-2621. Por qué Stripe rechazó una transferencia CAES que Backend sí creó.
 *
 * Existe porque `FAILED` a secas se pintaba "Fallido" en rojo, y casi nunca es un fallo de la consola
 * ni del endpoint: Backend aceptó el viaje (202), creó la transferencia, y es Stripe quien la rechaza
 * por la cuenta del conductor. Medido el 2026-10-01 sobre todo lo enviado desde la consola: de 19.230
 * transferencias `FAILED`, 18.084 son `insufficient_capabilities_for_transfer` y 93 un `rate_limit`
 * transitorio que Backend reintenta solo.
 *
 * **Vocabulario CERRADO y clasificado en BigQuery**, nunca en el front: `provider_result_message`
 * lleva emails y teléfonos reales, así que no sale de BigQuery. A Firestore solo llega la clave —y,
 * para `otro`, el `provider_result_code`—. Un motivo nuevo de Stripe cae en `otro` con su código: se
 * ve en pantalla en vez de esconderse.
 *
 * 2026-10-26: `cuenta_stripe_inexistente`, `alta_stripe_repetida` y `telefono_invalido`, y los números
 * fiscales dentro de `datos_personales_invalidos`. Eran los 57 viajes (189,60 €) que el pago del
 * 2026-10-22 dejó en `otro`: con ellos, nada de lo enviado desde la consola queda sin clasificar.
 */
export const MOTIVOS_FALLO_TRANSFERENCIA = [
    "cuenta_sin_capacidad",
    "usuario_borrado",
    "email_invalido",
    "codigo_postal_invalido",
    "telefono_invalido",
    "datos_personales_invalidos",
    "pais_no_soportado",
    "cuenta_stripe_inexistente",
    "alta_stripe_repetida",
    "limite_stripe_transitorio",
    "otro",
];
export const MotivoFalloTransferenciaSchema = z.enum(MOTIVOS_FALLO_TRANSFERENCIA);
/** Quién puede hacer algo para que esa transferencia salga. `ninguno` = no tiene arreglo desde aquí. */
export const QuienActuaSchema = z.enum(["conductor", "soporte", "backend", "ninguno"]);
/**
 * Las definiciones viven AQUÍ y solo aquí: front y back leen el mismo texto, así que la pantalla y
 * cualquier informe dicen lo mismo del mismo motivo.
 */
export const DEFINICIONES_MOTIVO_FALLO = {
    cuenta_sin_capacidad: {
        etiqueta: "Cuenta de Stripe sin capacidad de cobro",
        definicion: "La cuenta de Stripe del conductor no tiene activada la capacidad de recibir transferencias, normalmente porque no ha completado la verificación. Backend la reintenta, pero seguirá fallando hasta que el conductor termine el alta.",
        quienActua: "conductor",
        transitorio: false,
    },
    usuario_borrado: {
        etiqueta: "Usuario dado de baja",
        definicion: "Stripe no puede crear la cuenta del conductor porque sus datos están marcados como borrados en la app. No hay a quién pagar.",
        quienActua: "ninguno",
        transitorio: false,
    },
    email_invalido: {
        etiqueta: "Email del conductor inválido",
        definicion: "Stripe rechaza crear la cuenta del conductor porque el email guardado no es válido (por ejemplo, le falta el dominio). Lo tiene que corregir el conductor desde la app; Soporte tiene que avisarle.",
        quienActua: "conductor",
        transitorio: false,
    },
    codigo_postal_invalido: {
        etiqueta: "Código postal inválido",
        definicion: "Stripe rechaza crear la cuenta del conductor porque el código postal no es válido. Lo tiene que corregir el conductor desde la app; Soporte tiene que avisarle.",
        quienActua: "conductor",
        transitorio: false,
    },
    telefono_invalido: {
        etiqueta: "Teléfono del conductor inválido",
        definicion: "Stripe rechaza crear la cuenta del conductor porque el teléfono guardado no es un número completo. Lo tiene que corregir el conductor desde la app; Soporte tiene que avisarle.",
        quienActua: "conductor",
        transitorio: false,
    },
    datos_personales_invalidos: {
        etiqueta: "Datos personales inválidos",
        definicion: "Stripe rechaza los datos de identidad del conductor: el número de documento o el número fiscal (formato o longitud incorrectos) o la fecha de nacimiento (año anterior a 1900). Los tiene que corregir el conductor.",
        quienActua: "conductor",
        transitorio: false,
    },
    pais_no_soportado: {
        etiqueta: "País no soportado por Stripe",
        definicion: "El país del conductor no admite cuentas de Stripe creadas desde España. No se le puede transferir por esta vía.",
        quienActua: "ninguno",
        transitorio: false,
    },
    cuenta_stripe_inexistente: {
        etiqueta: "Cuenta de Stripe inexistente",
        definicion: "La cuenta de Stripe que Backend tiene guardada para el conductor ya no existe en Stripe, así que no hay a dónde transferir. Backend tiene que volver a crearla o borrar la que tiene guardada; reintentar la transferencia no basta.",
        quienActua: "backend",
        transitorio: false,
    },
    alta_stripe_repetida: {
        etiqueta: "Alta en Stripe repetida con otros datos",
        definicion: "Backend intenta dar de alta la cuenta de Stripe del conductor con la misma clave que en un intento anterior pero con datos distintos (normalmente porque el conductor los corrigió tras un alta fallida), y Stripe lo rechaza. Hay que revisarlo con Backend: puede que se resuelva cuando Stripe libere esa clave, pero no está comprobado.",
        quienActua: "backend",
        transitorio: false,
    },
    limite_stripe_transitorio: {
        etiqueta: "Límite de peticiones de Stripe",
        definicion: "Stripe frenó la transferencia por exceso de peticiones durante una ronda de pagos de Backend. Backend vuelve a intentar las transferencias fallidas en cada ronda, así que debería salir en la siguiente. Si sigue aquí después de varias rondas, hay que revisarlo con Backend.",
        quienActua: "backend",
        transitorio: true,
    },
    otro: {
        etiqueta: "Otro motivo de Stripe",
        definicion: "Un rechazo de Stripe que la consola todavía no clasifica. Se muestra su código para poder revisarlo con Backend.",
        quienActua: "backend",
        transitorio: false,
    },
};
/**
 * BB-2621. En qué punto está un viaje que el cruce ve todavía en `BLOCKED_BY_CERTIFICATION`.
 *
 * Existe porque ese bucket mezclaba tres cosas muy distintas (cruce del 2026-10-01: 129.836 viajes de
 * `91e31034…`, casi todos simplemente sin enviar todavía):
 *
 * - `no_enviado`: no consta como aceptado por Backend: su tanda aún no se ha pagado, o su petición
 *   falló de verdad (`confirm_trip_errors`).
 * - `enviado_tras_el_corte`: se envió después de la foto de transferencias que usa el cruce, o en
 *   las 2 h anteriores (`MARGEN_CORTE_HORAS`: el paso a PENDING lo hace el worker de Backend después
 *   del 202). Lo normal; mañana se verá movido.
 * - `aceptado_sin_mover`: se envió con margen ANTES de esa foto, Backend lo aceptó y no lo movió.
 *   **La alarma.**
 * - `sin_hora_de_envio`: confirmaciones anteriores a que el ledger guardara la hora real de envío
 *   (BB-2624). No se puede decidir entre los tres de arriba; se dice así en vez de adivinar.
 *
 * Dos más (2026-10-07) que salen del mensaje que Backend dejó en la transferencia. Backend no da un código
 * estable para ninguno de los dos: `provider_result_code` conserva el que tenía la transferencia antes,
 * así que se reconocen por el texto. Si ese texto cambia, los viajes vuelven a `aceptado_sin_mover`:
 * la alarma salta de más, nunca se esconde.
 *
 * - `revertido_por_blacklist`: Backend aceptó la certificación y la revirtió porque el conductor o el
 *   pasajero está en blacklist ("Certification rolled back: driver or passenger is blacklisted"). No es
 *   un fallo: es Backend aplicando su regla. Se decide ANTES que cualquier otro subestado, también en
 *   confirmaciones sin hora de envío, pero solo si la reversión es POSTERIOR a que encoláramos el viaje
 *   en esa confirmación: un mensaje viejo de un intento anterior no explica que este envío no moviera
 *   la transferencia, y tomarlo por benigno escondería la alarma.
 *   24.507 viajes enviados desde la consola a 2026-10-06, todos revertidos entre el 2026-09-30 y el
 *   2026-10-01. Fue una medida temporal de Backend mientras la consola no filtraba la blacklist: desde
 *   el 2026-10-01 no revierte, así que no deberían aparecer reversiones nuevas.
 * - `retenido_backfill`: la transferencia sigue con el mensaje del backfill de corte de julio ("Held for
 *   MITECO certification by the CAEs cutover backfill (TRI-48)") y no se ha tocado desde el
 *   2026-07-09, aunque la enviamos después y Backend la aceptó. **Sigue siendo una alarma**: es un
 *   `aceptado_sin_mover` con el origen identificado, y hay que llevarlo a Backend igual. El front no
 *   lo debe pintar como neutro. Solo sustituye a `aceptado_sin_mover`: un viaje sin hora de envío, no
 *   enviado o enviado tras el corte se queda en su subestado, porque no consta que Backend lo
 *   aceptara. Por eso los 72 de hoy (todos de confirmaciones anteriores a BB-2624) siguen saliendo
 *   como `sin_hora_de_envio`; el valor aplica a confirmaciones nuevas.
 */
export const SubestadoBloqueadoSchema = z.enum([
    "no_enviado",
    "enviado_tras_el_corte",
    "aceptado_sin_mover",
    "sin_hora_de_envio",
    "revertido_por_blacklist",
    "retenido_backfill",
]);
/** Los únicos estados donde una transferencia existe y fue rechazada: donde tiene sentido un motivo. */
export const ESTADOS_CON_MOTIVO_FALLO = ["FAILED", "CANCELED", "UNPROCESSABLE"];
/** Importe en euros como string decimal, igual que el resto del contrato: nunca se suma en JS. */
const ImporteEur = z.string().regex(/^-?\d+(\.\d+)?$/, "esperado un decimal como string");
export const ConfirmStatusMotivoSchema = z
    .object({
    motivo: MotivoFalloTransferenciaSchema,
    /** Solo con `motivo: 'otro'`: el código crudo de Stripe, para poder revisarlo. Nunca el mensaje. */
    providerResultCode: z.string().min(1).optional(),
    nTrips: z.number().int().nonnegative(),
    /** Conductores distintos (`credited_user_id`): a cuántas personas hay que contactar. */
    nUsuarios: z.number().int().nonnegative(),
    amountEur: ImporteEur,
})
    .strict();
export const ConfirmStatusSubestadoSchema = z
    .object({
    subestado: SubestadoBloqueadoSchema,
    nTrips: z.number().int().nonnegative(),
    amountEur: ImporteEur,
})
    .strict();
/**
 * Una fila de `confirm_status/{previewId}.byState` (BB-1642), con los desgloses de BB-2621.
 *
 * Sigue habiendo UNA fila por estado: los desgloses van dentro y suman exactamente el `nTrips` de la
 * fila, así que lo que ya pinta el front no cambia aunque no los lea. Los dos son `.optional()`: los
 * documentos que escribió la reconciliación antes de este cambio siguen siendo válidos.
 *
 * - `motivos`: solo en `FAILED`, `CANCELED` y `UNPROCESSABLE`.
 * - `subestados`: solo en `BLOCKED_BY_CERTIFICATION`.
 */
export const ConfirmStatusByStateSchema = z
    .object({
    state: z.string().min(1),
    nTrips: z.number().int().nonnegative(),
    /** null SOLO en el bucket `NO_TRANSFER`: no hay transferencia de la que sacar un importe. */
    amountEur: ImporteEur.nullable(),
    motivos: z.array(ConfirmStatusMotivoSchema).optional(),
    subestados: z.array(ConfirmStatusSubestadoSchema).optional(),
})
    .strict()
    // Cada desglose solo en su estado, también en el esquema y no solo en el código que lo escribe. Un
    // `NO_TRANSFER` con motivo diría "Stripe lo rechazó" sobre un viaje que ni tiene transferencia —
    // justo la alarma de BB-1642—, y un `PAID` con subestados no significa nada. Hallazgo del
    // payment-safety-reviewer sobre BB-2622, donde el SQL llegó a emitir eso.
    .superRefine((fila, ctx) => {
    if (fila.motivos !== undefined && !ESTADOS_CON_MOTIVO_FALLO.includes(fila.state)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["motivos"],
            message: `motivos solo en ${ESTADOS_CON_MOTIVO_FALLO.join("/")}, no en ${fila.state}`,
        });
    }
    if (fila.subestados !== undefined && fila.state !== "BLOCKED_BY_CERTIFICATION") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["subestados"],
            message: `subestados solo en BLOCKED_BY_CERTIFICATION, no en ${fila.state}`,
        });
    }
});
/**
 * BB-2621. Documento `estado_cola/caes-confirm-chunks`: el latido de la cola de pago (BB-2623).
 *
 * Existe porque la pantalla solo veía el silencio de CADA confirmación. Con dos en vuelo a la vez, la
 * cola (`maxConcurrentDispatches: 1`) puede servir casi solo a una durante minutos, y la otra se
 * pintaba "Sin resultado registrado — hay que reconciliarlo a mano" mientras la cola pagaba una tanda
 * cada 18 s sin un error (2026-10-01, `515afc5c…`). Con este documento el front distingue "esperando
 * turno" (quedan tandas y la cola avanza) de "la cola no avanza" (la alarma de verdad).
 *
 * Lo escribe el worker después de registrar cada tanda; el front lo lee directo con el SDK de cliente,
 * igual que `confirm_status`. Mutable a propósito, como `confirm_jobs`: el invariante 6 aplica solo a
 * `audit_log`.
 */
export const ConfirmacionActivaSchema = z
    .object({
    previewId: z.string().min(1),
    tandasPendientes: z.number().int().nonnegative(),
})
    .strict();
export const EstadoColaSchema = z
    .object({
    /** ISO 8601 — cuándo terminó la última tanda, de CUALQUIER confirmación. */
    ultimaTandaEn: z.string().datetime({ offset: true }),
    previewIdUltimaTanda: z.string().min(1),
    /**
     * Confirmaciones con tandas sin procesar. Una cancelada SIGUE aquí mientras le queden tandas: esas
     * tareas se despachan igual (y salen sin pagar), así que ocupan la cola. Vacío = nada pendiente.
     *
     * Dice que la confirmación tiene tandas, NO que avance: con dos en vuelo, una cuyas tandas fallan y
     * se reintentan sigue listada mientras la otra hace latir la cola. Quien pinte "esperando turno"
     * necesita además un tope de silencio por confirmación. Y una entrada puede quedarse aquí tras un
     * encolado parcial: si la confirmación ya tiene `outcome`, se ignora.
     */
    confirmacionesActivas: z.array(ConfirmacionActivaSchema),
})
    .strict();
//# sourceMappingURL=contract.js.map